import type { MessageClass } from './window.js';

/**
 * Per-24-hour caps (PLAN.md §4.6; owner decision 2026-10-12, question 4). Pure.
 *
 * A cap (a state rule such as Florida's 3 per 24 h, or a workspace override) counts the merchant's
 * outbound texts of the classes it applies to over a rolling 24 hours. Which classes count is DATA
 * (`state_rules.cap_applies_to`, `workspace_state_overrides.cap_applies_to`), so moving a state from
 * "every text" to "campaign and sequence texts only" needs no release. Until counsel reviews it, every
 * rule counts both classes.
 *
 * What counts (the caller selects it): texts handed to the provider or about to be (sending, sent,
 * delivered, failed, unknown, and queue rows already ready or leased), never blocked or cancelled ones,
 * and never the three required replies. Every cap must pass.
 */

export const CAP_WINDOW_MS = 24 * 60 * 60_000;

export interface CapRule {
  /** Who set it, for people: "Florida", "Workspace: all states". */
  label: string;
  max: number;
  appliesTo: readonly MessageClass[];
}

export interface CountedText {
  /** When it was handed to the provider (or now, for a text about to go). */
  at: Date;
  class: MessageClass;
}

export type CapResult = { ok: true } | { ok: false; rule: CapRule; count: number; until: Date };

/** Whether one more text of class `cls` fits every cap now; if not, when the strictest one frees up. */
export function checkCaps(
  cls: MessageClass,
  rules: readonly CapRule[],
  texts: readonly CountedText[],
  now: Date,
): CapResult {
  const since = now.getTime() - CAP_WINDOW_MS;
  let worst: { rule: CapRule; count: number; until: Date } | null = null;
  for (const rule of rules) {
    if (!rule.appliesTo.includes(cls)) continue;
    const times = texts
      .filter((t) => rule.appliesTo.includes(t.class) && t.at.getTime() > since)
      .map((t) => t.at.getTime())
      .sort((a, b) => a - b);
    if (times.length < rule.max) continue;
    // Enough of the oldest must age out to leave room for one more: the (n - max)-th oldest is the last.
    const last = times[times.length - rule.max] ?? now.getTime();
    const until = new Date(last + CAP_WINDOW_MS + 1);
    if (!worst || until > worst.until) worst = { rule, count: times.length, until };
  }
  return worst ? { ok: false, ...worst } : { ok: true };
}
